import React, { useEffect, useRef, useState } from "react";
import * as echarts from "echarts";

const ZScoreScatterChart = ({
  data = [], // [{ x, y, exception, zScore, group, row, categories }]
  flipAxes = false,
  useCategories = false,
  categories = [],
  xLabel = "X",
  yLabel = "Y",
  valueLabel = "Value",
}) => {
  const ref = useRef(null);
  const chartRef = useRef(null);
  const roRef = useRef(null);
  const [chartType, setChartType] = useState("scatter");
  const [animationInProgress, setAnimationInProgress] = useState(false);

  const totalItems = data.length;

  // Enhanced color scheme
  const colors = {
    normal: "#000",
    exception: "#ef4444",
    accent: "#8b5cf6",
    text: "#000",
    textLight: "#64748b",
    grid: "#e2e8f0",
    background: "#ffffff"
  };

  // Gradient colors for better visual appeal
  const normalGradient = {
    type: "linear",
    x: 0,
    y: 0,
    x2: 0,
    y2: 1,
    colorStops: [
      { offset: 0, color: "#60a5fa" },
      { offset: 0.7, color: "#3b82f6" },
      { offset: 1, color: "#1d4ed8" },
    ],
  };

  const exceptionGradient = {
    type: "linear",
    x: 0,
    y: 0,
    x2: 0,
    y2: 1,
    colorStops: [
      { offset: 0, color: "#f87171" },
      { offset: 0.7, color: "#ef4444" },
      { offset: 1, color: "#dc2626" },
    ],
  };

  // Initialize chart
  useEffect(() => {
    if (!ref.current) return;
    chartRef.current = echarts.init(ref.current, undefined, { 
      renderer: "canvas",
      backgroundColor: colors.background
    });

    // Enhanced resize handling
    if (window.ResizeObserver) {
      roRef.current = new ResizeObserver(() => {
        if (chartRef.current) {
          chartRef.current.resize();
        }
      });
      roRef.current.observe(ref.current);
    } else {
      const handleResize = () => chartRef.current && chartRef.current.resize();
      window.addEventListener("resize", handleResize);
      roRef.current = { 
        cleanup: () => window.removeEventListener("resize", handleResize) 
      };
    }

    return () => {
      try { 
        roRef.current?.disconnect?.(); 
      } catch (e) {}
      try { 
        roRef.current?.cleanup?.(); 
      } catch (e) {}
      try { 
        chartRef.current?.dispose?.(); 
      } catch (e) {}
      chartRef.current = null;
    };
  }, []);

  // Toggle chart type with animation protection
  const toggleChartType = () => {
    if (animationInProgress) return;
    setAnimationInProgress(true);
    setChartType((prev) => (prev === "scatter" ? "bar" : "scatter"));
    setTimeout(() => setAnimationInProgress(false), 800);
  };

  // Format values for display
  const formattedVal = (v) =>
    typeof v === "number" && isFinite(v) ? v.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }) : String(v ?? "N/A");

  // Main chart rendering effect
  useEffect(() => {
    if (!chartRef.current || !data || data.length === 0) return;

    const safeData = Array.isArray(data) ? data : [];
    if (safeData.length === 0) {
      chartRef.current.setOption({ 
        title: { 
          text: "No data available", 
          textStyle: { color: colors.text, fontSize: 16 },
          left: "center",
          top: "center"
        } 
      }, true);
      return;
    }

    // Compute ranges for dynamic symbol sizing
    let minVal = Infinity, maxVal = -Infinity;
    safeData.forEach((d) => {
      const valToUse = flipAxes ? d.x : d.y;
      if (typeof valToUse === "number" && isFinite(valToUse)) {
        minVal = Math.min(minVal, valToUse);
        maxVal = Math.max(maxVal, valToUse);
      }
    });
    
    if (minVal === Infinity) { 
      minVal = 0; 
      maxVal = 0; 
    }

    // Normalize symbol size based on value
    const normalizeSize = (v) => {
      if (maxVal === minVal) return 14;
      const scaled = ((v - minVal) / (maxVal - minVal)) * 15 + 10;
      return Math.max(8, Math.min(28, scaled));
    };

    // Separate normal and anomaly data
    const normalData = [];
    const exceptionData = [];
    
    safeData.forEach((d) => {
      const pointValue = [d.x, d.y];
      const payload = { 
        value: pointValue, 
        raw: d.row, 
        exception: d.exception, 
        zScore: d.zScore,
        group: d.group 
      };
      
      if (d.exception === 1) {
        exceptionData.push(payload);
      } else {
        normalData.push(payload);
      }
    });

    // Enhanced scatter series with better styling
    const scatterSeries = [
      {
        name: "No Exceptions",
        type: "scatter",
        data: normalData,
        symbolSize: (val) => {
          const num = flipAxes ? val[0] : val[1];
          return normalizeSize(num);
        },
        itemStyle: {
          color: normalGradient,
          borderColor: "#ffffff",
          borderWidth: 1.5,
          shadowBlur: 6,
          shadowColor: "rgba(0, 0, 0, 0.2)",
          opacity: 0.8
        },
        emphasis: {
          scale: true,
          scaleSize: 12,
          itemStyle: {
            shadowBlur: 10,
            shadowColor: "rgba(59, 130, 246, 0.5)"
          }
        }
      },
      {
        name: "Exceptions",
        type: "scatter",
        data: exceptionData,
        symbolSize: (val) => {
          const num = flipAxes ? val[0] : val[1];
          return normalizeSize(num) + 4; // Anomalies are slightly larger
        },
        itemStyle: {
          color: exceptionGradient,
          borderColor: "#ffffff",
          borderWidth: 2,
          shadowBlur: 8,
          shadowColor: "rgba(239, 68, 68, 0.5)",
          opacity: 0.9
        },
        emphasis: {
          scale: true,
          scaleSize: 16,
          itemStyle: {
            shadowBlur: 12,
            shadowColor: "rgba(239, 68, 68, 0.6)"
          }
        }
      }
    ];

    // Enhanced axis configuration
    const xAxisConfig = {
      type: useCategories && !flipAxes ? "category" : "value",
      name: xLabel,
      nameLocation: "middle",
      nameGap: 30,
      nameTextStyle: { 
        color: colors.text, 
        fontWeight: "600",
        fontSize: 12
      },
      axisLine: { 
        lineStyle: { 
          color: colors.text, 
          width: 2 
        } 
      },
      axisTick: {
        lineStyle: { color: colors.text }
      },
      axisLabel: { 
        color: colors.text,
        fontSize: 11
      },
      splitLine: {
        lineStyle: {
          color: colors.grid,
          type: "dashed"
        }
      },
      ...(useCategories && !flipAxes && {
        data: categories,
        axisLabel: {
          color: colors.text,
          fontSize: 11,
          formatter: (value) => {
            if (typeof value === 'string' && value.length > 15) {
              return value.substring(0, 15) + '...';
            }
            return value;
          }
        }
      })
    };

    const yAxisConfig = {
      type: useCategories && flipAxes ? "category" : "value",
      // name: yLabel,
      // nameLocation: "middle",
      // nameGap: 45,
      // nameTextStyle: { 
      //   color: colors.text, 
      //   fontWeight: "600",
      //   fontSize: 12
      // },
      axisLine: { 
        lineStyle: { 
          color: colors.text, 
          width: 2 
        } 
      },
      axisTick: {
        lineStyle: { color: colors.text }
      },
      axisLabel: { 
        color: colors.text,
        fontSize: 11
      },
      splitLine: {
        lineStyle: {
          color: colors.grid,
          type: "dashed"
        }
      },
      ...(useCategories && flipAxes && {
        data: categories,
        axisLabel: {
          color: colors.text,
          fontSize: 11,
          formatter: (value) => {
            if (typeof value === 'string' && value.length > 20) {
              return value.substring(0, 18) + '...';
            }
            return value;
          }
        }
      })
    };

    // Enhanced tooltip formatter
    const tooltipFormatter = function (params) {
      const d = params.data || {};
      const value = Array.isArray(d.value) ? d.value : [0, 0];
      const xVal = value[0];
      const yVal = value[1];
      const raw = d.raw || {};
      const isException = d.exception === 1;

      let tooltipContent = `
        <div style="padding: 8px; background: white; border-radius: 6px; box-shadow: 0 4px 12px rgba(0,0,0,0.15);">
          <div style="font-weight: 700; color: #1f2937; margin-bottom: 6px; border-bottom: 1px solid #e5e7eb; padding-bottom: 4px;">
            ${d.group || "Data Point"}
          </div>
          <div style="color: #374151;">
            <div><strong>${xLabel}:</strong> ${formattedVal(xVal)}</div>
            <div><strong>${yLabel}:</strong> ${formattedVal(yVal)}</div>
            <div style="margin-top: 4px;">
              <strong>Status:</strong> 
              <span style="color: ${isException ? '#ef4444' : '#10b981'}; font-weight: 600; margin-left: 4px;">
                ${isException ? '🚨 ANOMALY' : '✓ NORMAL'}
              </span>
            </div>
      `;

      if (d.zScore !== undefined && d.zScore !== 0) {
        tooltipContent += `<div><strong>Z-Score:</strong> ${formattedVal(d.zScore)}</div>`;
      }

      // Add additional row data if available
      const additionalFields = Object.keys(raw).slice(0, 3); // Show first 3 fields
      if (additionalFields.length > 0) {
        tooltipContent += `<div style="margin-top: 6px; padding-top: 4px; border-top: 1px solid #e5e7eb;">`;
        additionalFields.forEach(field => {
          if (!['x', 'y', 'exception', 'zScore', 'group'].includes(field)) {
            tooltipContent += `<div><small>${field}: ${formattedVal(raw[field])}</small></div>`;
          }
        });
        tooltipContent += `</div>`;
      }

      tooltipContent += `</div></div>`;
      return tooltipContent;
    };

    // Complete chart option configuration
    const option = {
      backgroundColor: colors.background,
      title: { 
        text: "Z-Score Anomaly Detection", 
        left: "center", 
        top: 10,
        textStyle: { 
          color: colors.text, 
          fontSize: 16,
          fontWeight: "bold"
        } 
      },
      tooltip: {
        trigger: "item",
        formatter: tooltipFormatter,
        backgroundColor: "transparent",
        borderWidth: 0,
        textStyle: {
          color: colors.text
        }
      },
      legend: { 
        data: ["No Exceptions", "Exceptions"], 
        top: 40,
        textStyle: {
          color: colors.text
        },
        itemStyle: {
          borderWidth: 0
        }
      },
      grid: { 
        left: "8%", 
        right: "5%", 
        bottom: "15%", 
        top: 80, 
        containLabel: true 
      },
      xAxis: xAxisConfig,
      yAxis: yAxisConfig,
      dataZoom: [
        { 
          type: "slider", 
          xAxisIndex: [0], 
          start: 0, 
          end: Math.min(100, ((500 / Math.max(totalItems, 1)) * 100)),
          height: 20, 
          bottom: 5,
          textStyle: { color: colors.text },
          handleStyle: {
            color: colors.accent
          }
        },
        { 
          type: "inside", 
          xAxisIndex: [0] 
        },
      ],
      series: scatterSeries
    };

    chartRef.current.setOption(option, { notMerge: true });
  }, [data, xLabel, yLabel, categories, flipAxes, useCategories, chartType]);

  return (
    <div style={{ 
      position: "relative", 
      width: "100%", 
      height: "100%",
      background: colors.background,
      borderRadius: "8px"
    }}>
      <div ref={ref} style={{ width: "100%", height: "100%" }} />
      
      {/* Enhanced toggle button */}
      <button
        onClick={toggleChartType}
        disabled={animationInProgress}
        style={{
          position: "absolute",
          top: 12,
          right: 12,
          padding: "8px 16px",
          backgroundColor: colors.accent,
          color: "white",
          border: "none",
          borderRadius: "6px",
          cursor: animationInProgress ? "not-allowed" : "pointer",
          fontWeight: "600",
          fontSize: "12px",
          boxShadow: "0 2px 8px rgba(139, 92, 246, 0.3)",
          transition: "all 0.2s ease",
          opacity: animationInProgress ? 0.7 : 1
        }}
        onMouseOver={(e) => {
          if (!animationInProgress) {
            e.target.style.backgroundColor = "#7c3aed";
            e.target.style.transform = "translateY(-1px)";
          }
        }}
        onMouseOut={(e) => {
          if (!animationInProgress) {
            e.target.style.backgroundColor = colors.accent;
            e.target.style.transform = "translateY(0px)";
          }
        }}
      >
        {chartType === "scatter" ? "📊 Toggle View" : "🔍 Toggle View"}
      </button>

      {/* Data summary badge */}
      {data.length > 0 && (
        <div style={{
          position: "absolute",
          top: 12,
          left: 12,
          padding: "6px 12px",
          backgroundColor: "rgba(255, 255, 255, 0.9)",
          color: colors.text,
          border: `1px solid ${colors.grid}`,
          borderRadius: "6px",
          fontSize: "12px",
          fontWeight: "600",
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
        }}>
          📈 {data.length} points
          {data.filter(d => d.exception === 1).length > 0 && (
            <span style={{ color: "#ef4444", marginLeft: "8px" }}>
              🚨 {data.filter(d => d.exception === 1).length} anomalies
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default ZScoreScatterChart;